import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Bug,
  FileText,
  Activity,
  Play,
  RefreshCw,
  BarChart3,
  Link2,
  MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageSpinner } from '@/components/ui/Spinner';
import { adminApi } from '@/api/endpoints/admin';
import { metrosApi, type Metro } from '@/api/endpoints/metros';
import { barsApi, type Bar } from '@/api/endpoints/bars';

function formatBytes(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '0 B';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function ScriptsPage() {
  const queryClient = useQueryClient();

  // State for script parameters
  const [selectedMetroId, setSelectedMetroId] = useState<string>('');
  const [crawlBarId, setCrawlBarId] = useState<string>('');
  const [extractBarId, setExtractBarId] = useState<string>('');

  // Fetch metros for discovery dropdown
  const { data: metrosData, isLoading: metrosLoading } = useQuery({
    queryKey: ['metros'],
    queryFn: metrosApi.list
  });

  // Fetch job queue status
  const { data: jobStatusData, isLoading: jobStatusLoading, refetch: refetchJobStatus } = useQuery({
    queryKey: ['job-status'],
    queryFn: adminApi.getJobQueueStatus,
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  // Fetch bars for search (for specific bar operations)
  const { data: barsData } = useQuery({
    queryKey: ['bars-list'],
    queryFn: () => barsApi.list({ limit: 500 })
  });

  // Fetch content metrics
  const { data: metricsData, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery({
    queryKey: ['content-metrics'],
    queryFn: () => adminApi.getContentMetrics(7)
  });

  const metros = metrosData?.metros || [];
  const bars = barsData?.bars || [];
  const jobStatus = jobStatusData?.queue;

  // Pipeline Mutations
  const discoverMutation = useMutation({
    mutationFn: (metroId: string) => metrosApi.discover(metroId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      queryClient.invalidateQueries({ queryKey: ['job-status'] });
      const metro = metros.find((m: Metro) => m.id === selectedMetroId);
      toast.success(`Discovery started for ${metro?.name || 'metro'}`);
    },
    onError: () => {
      toast.error('Failed to start discovery');
    }
  });

  const crawlMutation = useMutation({
    mutationFn: (barId?: string) => adminApi.triggerCrawl(barId ? { barId } : {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['job-status'] });
      if (data.enqueued !== undefined) {
        toast.success(`Enqueued ${data.enqueued} bars for crawling`);
      } else {
        toast.success('Crawl completed');
      }
    },
    onError: () => {
      toast.error('Failed to start crawling');
    }
  });

  const extractMutation = useMutation({
    // Single-bar extraction is a deliberate action → force (bypass cache).
    // "Extract All Needing" (no barId) stays non-forcing.
    mutationFn: (barId?: string) => adminApi.triggerExtraction(barId ? { barId, force: true } : {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['job-status'] });
      if (data.enqueued !== undefined) {
        toast.success(`Enqueued ${data.enqueued} bars for extraction`);
      } else {
        toast.success('Extraction completed');
      }
    },
    onError: () => {
      toast.error('Failed to start extraction');
    }
  });

  // SEO Script Mutations
  const generateSlugsMutation = useMutation({
    mutationFn: adminApi.runGenerateSlugs,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bars'] });
      toast.success(data.message);
    },
    onError: () => {
      toast.error('Failed to generate slugs');
    }
  });

  const discoverLocationsMutation = useMutation({
    mutationFn: adminApi.runDiscoverLocations,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success(data.message);
    },
    onError: () => {
      toast.error('Failed to discover locations');
    }
  });

  function handleDiscovery() {
    if (!selectedMetroId) {
      toast.error('Please select a metro');
      return;
    }
    discoverMutation.mutate(selectedMetroId);
  }

  function handleCrawl() {
    crawlMutation.mutate(crawlBarId || undefined);
    setCrawlBarId('');
  }

  function handleExtract() {
    extractMutation.mutate(extractBarId || undefined);
    setExtractBarId('');
  }

  if (metrosLoading) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Scripts</h1>
        <p className="text-[var(--color-muted)]">Run backend operations from this page</p>
      </div>

      {/* Pipeline Operations */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Discovery */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-500" />
              Discovery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[var(--color-muted)]">
              Discover bars in a metro area using Google Places API
            </p>
            <select
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              value={selectedMetroId}
              onChange={(e) => setSelectedMetroId(e.target.value)}
            >
              <option value="">Select a metro...</option>
              {metros.map((metro: Metro) => (
                <option key={metro.id} value={metro.id}>
                  {metro.name} ({metro.barCount || 0} bars)
                </option>
              ))}
            </select>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleDiscovery}
              disabled={!selectedMetroId || discoverMutation.isPending}
            >
              <Play className="w-4 h-4 mr-2" />
              {discoverMutation.isPending ? 'Running...' : 'Run Discovery'}
            </Button>
          </CardContent>
        </Card>

        {/* Crawling */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-green-500" />
              Crawling
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[var(--color-muted)]">
              Find specials/events URLs on bar websites
            </p>
            <Input
              placeholder="Bar ID (optional - leave empty for all)"
              value={crawlBarId}
              onChange={(e) => setCrawlBarId(e.target.value)}
              list="bars-crawl"
            />
            <datalist id="bars-crawl">
              {bars.slice(0, 100).map((bar: Bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name}
                </option>
              ))}
            </datalist>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleCrawl}
              disabled={crawlMutation.isPending}
            >
              <Play className="w-4 h-4 mr-2" />
              {crawlMutation.isPending ? 'Running...' : crawlBarId ? 'Crawl Bar' : 'Crawl All Needing'}
            </Button>
          </CardContent>
        </Card>

        {/* Extraction */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Extraction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[var(--color-muted)]">
              Extract specials & events from crawled websites
            </p>
            <Input
              placeholder="Bar ID (optional - leave empty for all)"
              value={extractBarId}
              onChange={(e) => setExtractBarId(e.target.value)}
              list="bars-extract"
            />
            <datalist id="bars-extract">
              {bars.slice(0, 100).map((bar: Bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name}
                </option>
              ))}
            </datalist>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleExtract}
              disabled={extractMutation.isPending}
            >
              <Play className="w-4 h-4 mr-2" />
              {extractMutation.isPending ? 'Running...' : extractBarId ? 'Extract Bar' : 'Extract All Needing'}
            </Button>
          </CardContent>
        </Card>

        {/* Job Queue Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-amber-500" />
              Job Queue
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => refetchJobStatus()}
                disabled={jobStatusLoading}
              >
                <RefreshCw className={`w-4 h-4 ${jobStatusLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {jobStatus ? (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{jobStatus.totals?.pending || 0}</div>
                    <div className="text-[var(--color-muted)]">Pending</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{jobStatus.totals?.running || 0}</div>
                    <div className="text-[var(--color-muted)]">Running</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{jobStatus.last24h?.completed || 0}</div>
                    <div className="text-[var(--color-muted)]">Done 24h</div>
                  </div>
                </div>
                {jobStatus.last24h?.failed > 0 && (
                  <div className="text-red-500 text-center">
                    {jobStatus.last24h.failed} failed in last 24h
                  </div>
                )}
                <div className="text-xs text-[var(--color-muted)] space-y-1">
                  <div>Crawling: {jobStatus.byType?.crawling?.pending || 0} pending, {jobStatus.byType?.crawling?.running || 0} running</div>
                  <div>Extraction: {jobStatus.byType?.extraction?.pending || 0} pending, {jobStatus.byType?.extraction?.running || 0} running</div>
                  <div>Discovery: {jobStatus.byType?.discovery?.pending || 0} pending, {jobStatus.byType?.discovery?.running || 0} running</div>
                </div>
              </div>
            ) : (
              <div className="text-[var(--color-muted)] text-center py-4">
                Loading job status...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Metrics */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-500" />
              Content Metrics (7 days)
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => refetchMetrics()}
                disabled={metricsLoading}
              >
                <RefreshCw className={`w-4 h-4 ${metricsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metricsData ? (
              <div className="space-y-4">
                {/* Aggregates */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{metricsData.aggregates?.totalRequests?.toLocaleString() || 0}</div>
                    <div className="text-[var(--color-muted)]">Total Requests</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{formatBytes(metricsData.aggregates?.avgOriginalSize)}</div>
                    <div className="text-[var(--color-muted)]">Avg Original</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{formatBytes(metricsData.aggregates?.avgSentSize)}</div>
                    <div className="text-[var(--color-muted)]">Avg Sent</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[var(--color-border)]">
                    <div className="font-bold text-lg">{formatBytes(metricsData.aggregates?.maxOriginalSize)}</div>
                    <div className="text-[var(--color-muted)]">Max Size</div>
                  </div>
                </div>

                {/* Truncation Analysis */}
                {metricsData.truncationAnalysis && (
                  <div className="border-t border-[var(--color-border)] pt-3">
                    <h4 className="text-sm font-medium mb-2">Truncation Analysis (30 days)</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center p-2 rounded bg-[var(--color-border)]">
                        <div className="font-bold">{metricsData.truncationAnalysis.truncatedCount?.toLocaleString() || 0}</div>
                        <div className="text-xs text-[var(--color-muted)]">Truncated</div>
                      </div>
                      <div className="text-center p-2 rounded bg-[var(--color-border)]">
                        <div className="font-bold">{metricsData.truncationAnalysis.notTruncatedCount?.toLocaleString() || 0}</div>
                        <div className="text-xs text-[var(--color-muted)]">Not Truncated</div>
                      </div>
                      <div className="text-center p-2 rounded bg-[var(--color-border)]">
                        <div className="font-bold text-amber-500">
                          {metricsData.truncationAnalysis.truncationRate
                            ? `${(parseFloat(metricsData.truncationAnalysis.truncationRate) * 100).toFixed(1)}%`
                            : '0%'}
                        </div>
                        <div className="text-xs text-[var(--color-muted)]">Truncation Rate</div>
                      </div>
                    </div>
                    {metricsData.truncationAnalysis.avgBytesLost > 0 && (
                      <div className="text-xs text-[var(--color-muted)] mt-2 text-center">
                        Avg bytes lost when truncated: {formatBytes(metricsData.truncationAnalysis.avgBytesLost)}
                      </div>
                    )}
                  </div>
                )}

                {/* Size Distribution */}
                {metricsData.distribution && metricsData.distribution.length > 0 && (
                  <div className="border-t border-[var(--color-border)] pt-3">
                    <h4 className="text-sm font-medium mb-2">Size Distribution</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-xs">
                      {metricsData.distribution.map((bucket: { bucket: string; count: number }) => (
                        <div key={bucket.bucket} className="flex justify-between p-1 rounded bg-[var(--color-border)]">
                          <span className="text-[var(--color-muted)]">{bucket.bucket}</span>
                          <span className="font-medium">{bucket.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-[var(--color-muted)] text-center py-4">
                {metricsLoading ? 'Loading metrics...' : 'No metrics data available'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEO Scripts */}
      <div className="border-t border-[var(--color-border)] pt-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-blue-500" />
          SEO Scripts
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Generate Bar Slugs */}
          <Card className="border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-500" />
                Generate Bar Slugs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[var(--color-muted)]">
                Generate SEO-friendly URL slugs for all bars that don't have one. Uses bar name first, appends city if needed.
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => generateSlugsMutation.mutate()}
                disabled={generateSlugsMutation.isPending}
              >
                <Play className="w-4 h-4 mr-2" />
                {generateSlugsMutation.isPending ? 'Generating...' : 'Generate Slugs'}
              </Button>
              {generateSlugsMutation.data && (
                <div className="text-xs text-[var(--color-muted)] p-2 rounded bg-[var(--color-border)]">
                  Last run: {generateSlugsMutation.data.results.success} generated, {generateSlugsMutation.data.results.errors} errors
                </div>
              )}
            </CardContent>
          </Card>

          {/* Discover Locations */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-500" />
                Discover Locations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[var(--color-muted)]">
                Auto-discover cities from bar addresses and create location landing pages. Calculates center coordinates for each city.
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => discoverLocationsMutation.mutate()}
                disabled={discoverLocationsMutation.isPending}
              >
                <Play className="w-4 h-4 mr-2" />
                {discoverLocationsMutation.isPending ? 'Discovering...' : 'Discover Locations'}
              </Button>
              {discoverLocationsMutation.data && (
                <div className="text-xs text-[var(--color-muted)] p-2 rounded bg-[var(--color-border)]">
                  Last run: {discoverLocationsMutation.data.results.created} created, {discoverLocationsMutation.data.results.updated} updated, {discoverLocationsMutation.data.results.skipped} skipped
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
